/**
 * Quizzes hub — landing page that groups quizzes by difficulty
 * (Foundations / Intermediate / Advanced) and shows ONE category at a
 * time via tabs.
 *
 * The full 17-quiz list was too long to scroll; tabs solve that
 * without hiding any quizzes from the user. The active tab is
 * persisted to localStorage so returning users land on their
 * preferred difficulty level.
 *
 * Adding a new quiz: append one entry to QUIZZES with a `difficulty`
 * tag. Route registration in App.tsx is separate.
 */

import type { LucideIcon } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { Brain, Crosshair, Flag, Flame, NavIcons, RefreshCw, Sigma, Sparkles, Target, Users } from "@/shared/components/icons";
import { PageHeader } from "@/shared/components/layout/PageHeader";

type Difficulty = "foundations" | "intermediate" | "advanced";

interface QuizListing {
  path: string;
  title: string;
  blurb: string;
  source: string;
  Icon: LucideIcon;
  difficulty: Difficulty;
}

const QUIZZES: QuizListing[] = [
  // FOUNDATIONS ------------------------------------------------------
  { path: "/quizzes/open-fold", title: "Open / Fold", blurb: "Preflop range trainer. Position + hand → open or fold?", source: "Sheet 15 ranges", Icon: Crosshair, difficulty: "foundations" },
  { path: "/quizzes/wider-narrower", title: "Wider or Narrower?", blurb: "Two positions: which opens more hands?", source: "Sheet 15 ranges", Icon: Target, difficulty: "foundations" },
  { path: "/quizzes/position-ordering", title: "Position Ordering", blurb: "Of 4 positions, which has the tightest or widest range?", source: "Sheet 1 frequencies", Icon: Target, difficulty: "foundations" },
  { path: "/quizzes/bb-defend", title: "BB Defend", blurb: "Call / 3-bet / fold from the Big Blind vs an open.", source: "Sheet 1 + 15", Icon: Crosshair, difficulty: "foundations" },
  { path: "/quizzes/pick-sizing", title: "Pick the Sizing", blurb: "Open / 3-bet / 4-bet rules from sheet 1.", source: "Sheet 1 sizing", Icon: Flame, difficulty: "foundations" },
  { path: "/quizzes/count-outs", title: "Count the Outs", blurb: "Show a flop draw → how many outs do you have?", source: "Standard out counts", Icon: Sigma, difficulty: "foundations" },
  { path: "/quizzes/rule-of-4-2", title: "Rule of 4 and 2", blurb: "Outs × 4 on the flop, × 2 on the turn → approximate equity.", source: "Standard equity shortcut", Icon: Sigma, difficulty: "foundations" },

  // INTERMEDIATE -----------------------------------------------------
  { path: "/quizzes/pot-odds", title: "Pot Odds", blurb: "Bet sizing → equity needed to call.", source: "Sheet 5 defending math", Icon: Brain, difficulty: "intermediate" },
  { path: "/quizzes/spr", title: "SPR", blurb: "Effective stack ÷ pot: find the right number to play around.", source: "Stack math", Icon: Sigma, difficulty: "intermediate" },
  { path: "/quizzes/range-advantage", title: "Range Advantage", blurb: "Single-raised pot, BTN vs BB: who's ahead on this flop?", source: "Sheet 3 cheat sheet", Icon: Flame, difficulty: "intermediate" },
  { path: "/quizzes/identify-archetype", title: "Identify the Archetype", blurb: "Read a vignette, pick the player archetype.", source: "Sheets 9–14", Icon: Users, difficulty: "intermediate" },
  { path: "/quizzes/exploit-direction", title: "Exploit Direction", blurb: "vs <archetype>: bluff more, less, or same as baseline?", source: "Sheets 8–14", Icon: Sparkles, difficulty: "intermediate" },
  { path: "/quizzes/hand-role", title: "Pick the Hand Role", blurb: "Classify the bet: value / bluff / semi-bluff / give up.", source: "Sheet 3 framework", Icon: Target, difficulty: "intermediate" },

  // ADVANCED ---------------------------------------------------------
  { path: "/quizzes/mdf", title: "MDF", blurb: "Minimum Defence Frequency: how much of your range must defend?", source: "Sheet 5 defending math", Icon: Brain, difficulty: "advanced" },
  { path: "/quizzes/bluff-ratio", title: "Bluff Ratio", blurb: "Bet sizing → % bluffs villain needs for a balanced range.", source: "Sheet 5 defending math", Icon: Brain, difficulty: "advanced" },
  { path: "/quizzes/reversion-trigger", title: "Reversion Triggers", blurb: "Read shifted: hold, drop magnitude, or revert to GTO?", source: "Sheet 8 switching back", Icon: RefreshCw, difficulty: "advanced" },
  { path: "/quizzes/cost-of-error", title: "Cost-of-Error", blurb: "How much evidence does each exploit need?", source: "Sheet 8 cost matrix", Icon: Flag, difficulty: "advanced" },
];

interface DifficultyMeta {
  label: string;
  blurb: string;
  // Tailwind class scoped to this difficulty's active-tab + section accents.
  accent: string;
  /** Tailwind class for the tinted active-tab background. */
  activeBg: string;
  /** Tailwind class for the active-tab border. */
  activeBorder: string;
}

const DIFFICULTY_META: Record<Difficulty, DifficultyMeta> = {
  foundations: {
    label: "Foundations",
    blurb: "Core skills every player needs locked in.",
    accent: "text-emerald-400",
    activeBg: "bg-emerald-400/10",
    activeBorder: "border-emerald-400/50",
  },
  intermediate: {
    label: "Intermediate",
    blurb: "The math + concepts that separate thinking players from rec play.",
    accent: "text-sky-400",
    activeBg: "bg-sky-400/10",
    activeBorder: "border-sky-400/50",
  },
  advanced: {
    label: "Advanced",
    blurb: "Solver-era / GTO-fluent topics. Not everyone needs these.",
    accent: "text-violet-400",
    activeBg: "bg-violet-400/10",
    activeBorder: "border-violet-400/50",
  },
};

const DIFFICULTY_ORDER: Difficulty[] = ["foundations", "intermediate", "advanced"];

// --- Tab persistence ----------------------------------------------------

const TAB_STORAGE_KEY = "pt:quizzes:tab";

function loadActiveTab(): Difficulty {
  try {
    const raw = localStorage.getItem(TAB_STORAGE_KEY);
    if (raw === "foundations" || raw === "intermediate" || raw === "advanced") {
      return raw;
    }
  } catch {
    // localStorage unavailable; fall through to default.
  }
  return "foundations";
}

function saveActiveTab(d: Difficulty): void {
  try {
    localStorage.setItem(TAB_STORAGE_KEY, d);
  } catch {
    // No-op — UI state, not load-bearing.
  }
}

// --- Component ----------------------------------------------------------

export function QuizzesHub() {
  const [active, setActive] = useState<Difficulty>(loadActiveTab);

  const handleSelect = (d: Difficulty) => {
    setActive(d);
    saveActiveTab(d);
  };

  const activeQuizzes = QUIZZES.filter((q) => q.difficulty === active);
  const meta = DIFFICULTY_META[active];

  return (
    <div className="max-w-2xl md:max-w-4xl mx-auto px-4 md:px-8 py-8 space-y-6">
      <PageHeader
        Icon={NavIcons.quizzes}
        title="Quizzes"
        subtitle="Bite-sized drills derived from the study sheets."
      />

      <DifficultyTabs active={active} onSelect={handleSelect} />

      <div className="space-y-1">
        <div className={`text-[11px] uppercase tracking-[0.12em] font-semibold ${meta.accent}`}>
          {meta.label}
        </div>
        <p className="text-xs text-text-muted">{meta.blurb}</p>
      </div>

      <div className="grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {activeQuizzes.map((q) => (
          <Link
            key={q.path}
            to={q.path}
            className="rounded-2xl border border-border bg-surface p-5 hover:border-accent/40 hover:bg-accent/[0.04] transition group flex flex-col gap-3"
          >
            <div className="rounded-xl bg-accent/15 text-accent p-2.5 group-hover:bg-accent/20 transition self-start">
              <q.Icon size={22} strokeWidth={1.75} />
            </div>
            <div>
              <div className="text-base font-semibold text-text tracking-tight">{q.title}</div>
              <div className="text-sm text-text-muted mt-1 line-clamp-3">{q.blurb}</div>
            </div>
            <div className="text-[10px] text-text-muted mt-auto uppercase tracking-wider">
              {q.source}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function DifficultyTabs({
  active,
  onSelect,
}: {
  active: Difficulty;
  onSelect: (d: Difficulty) => void;
}) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {DIFFICULTY_ORDER.map((d) => {
        const meta = DIFFICULTY_META[d];
        const isActive = d === active;
        const classes = [
          "py-3 px-2 rounded-xl border text-xs sm:text-sm font-semibold transition active:scale-[0.98]",
          isActive
            ? `${meta.accent} ${meta.activeBg} ${meta.activeBorder}`
            : "text-text-muted border-border bg-surface hover:border-text/40 hover:text-text",
        ].join(" ");
        return (
          <button key={d} type="button" onClick={() => onSelect(d)} className={classes}>
            {meta.label}
          </button>
        );
      })}
    </div>
  );
}
